/**
 * Provider Response Flow
 * Handles ACCEPT / DECLINE / PROPOSE NEW TIME for booking requests
 * Part of the Smart Booking & Quick Response Engine
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { 
  CheckCircle, X, Clock, Calendar as CalendarIcon, 
  MapPin, Zap, User, Mail, Phone, ArrowLeft,
  Loader2, AlertTriangle, MessageSquare, Euro
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Provider Response Page
 * Accessed via /booking/respond/{bookingId}?token={providerToken}
 */
export const ProviderResponsePage = () => {
  const { t, language } = useLanguage();
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState(null);
  const [responseSubmitted, setResponseSubmitted] = useState(false);
  const [responseType, setResponseType] = useState(null);

  // Propose new time modal
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [proposedDate, setProposedDate] = useState(null);
  const [proposedTime, setProposedTime] = useState('');
  const [proposedMessage, setProposedMessage] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');

  useEffect(() => {
    if (bookingId && token) {
      fetchBookingDetails();
    } else {
      setError('Invalid booking link');
      setLoading(false);
    }
  }, [bookingId, token]);

  const fetchBookingDetails = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/bookings/provider-view/${bookingId}?token=${token}`
      );

      if (!response.ok) {
        throw new Error('Booking not found or link expired');
      }

      const data = await response.json();
      
      // API returns { booking: {...}, already_responded: bool, existing_response: {...} }
      setBooking(data.booking);

      // Check if already responded
      if (data.already_responded && data.existing_response) {
        setResponseSubmitted(true);
        setResponseType(data.existing_response.response_type);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (type, additionalData = {}) => {
    setResponding(true);
    try {
      const response = await fetch(
        `${API_URL}/api/bookings/respond/${bookingId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            response_type: type,
            ...additionalData
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit response');
      }

      setResponseSubmitted(true);
      setResponseType(type);
      setShowProposeModal(false);

      const messages = {
        accept: { en: 'Booking accepted! Client has been notified.', et: 'Broneering vastu võetud! Klienti on teavitatud.', fi: 'Varaus hyväksytty! Asiakkaalle on ilmoitettu.' },
        decline: { en: 'Booking declined. Client has been notified.', et: 'Broneering keeldutud. Klienti on teavitatud.', fi: 'Varaus hylätty. Asiakkaalle on ilmoitettu.' },
        propose: { en: 'Alternative time proposed! Waiting for client approval.', et: 'Alternatiivne aeg pakutud! Ootame kliendi kinnitust.', fi: 'Vaihtoehtoinen aika ehdotettu! Odotetaan asiakkaan hyväksyntää.' }
      };

      toast.success(messages[type][language] || messages[type].en);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResponding(false);
    }
  };

  const handleAccept = () => handleResponse('accept');
  const handleDecline = () => handleResponse('decline');
  const handlePropose = () => {
    if (!proposedDate) {
      toast.error(t('booking.response.selectDate') || 'Please select a date');
      return;
    }
    handleResponse('propose', {
      proposed_date: format(proposedDate, 'yyyy-MM-dd'),
      proposed_time: proposedTime,
      message: proposedMessage,
      estimated_price: proposedPrice ? parseFloat(proposedPrice) : null
    });
  };

  const labels = {
    en: {
      title: 'Booking Request',
      from: 'From',
      service: 'Service',
      date: 'Requested Date',
      time: 'Preferred Time',
      location: 'Location',
      description: 'Description',
      urgency: 'Urgency',
      quickResponse: 'Quick Response',
      accept: 'Accept',
      decline: 'Decline',
      proposeNewTime: 'Propose New Time',
      accepted: 'You accepted this booking',
      declined: 'You declined this booking',
      proposed: 'You proposed an alternative time',
      waitingClient: 'Waiting for client approval',
      proposeTitle: 'Propose Alternative Time',
      proposeDate: 'New Date',
      proposeTime: 'New Time',
      proposeMessage: 'Message to Client',
      proposePrice: 'Estimated Price (optional)',
      submit: 'Submit Proposal',
      cancel: 'Cancel',
      expired: 'This booking link has expired',
      alreadyResponded: 'You have already responded to this request'
    },
    et: {
      title: 'Broneerimispäring',
      from: 'Kellelt',
      service: 'Teenus',
      date: 'Soovitud kuupäev',
      time: 'Eelistatud aeg',
      location: 'Asukoht',
      description: 'Kirjeldus',
      urgency: 'Kiirus',
      quickResponse: 'Kiirvasuse',
      accept: 'Nõustu',
      decline: 'Keeldu',
      proposeNewTime: 'Paku uut aega',
      accepted: 'Sa nõustusid selle broneeringuga',
      declined: 'Sa keeldusid sellest broneeringust',
      proposed: 'Sa pakkusid alternatiivse aja',
      waitingClient: 'Ootame kliendi kinnitust',
      proposeTitle: 'Paku alternatiivne aeg',
      proposeDate: 'Uus kuupäev',
      proposeTime: 'Uus aeg',
      proposeMessage: 'Sõnum kliendile',
      proposePrice: 'Hinnanguline hind (valikuline)',
      submit: 'Saada ettepanek',
      cancel: 'Tühista',
      expired: 'See broneeringu link on aegunud',
      alreadyResponded: 'Sa oled sellele päringule juba vastanud'
    },
    fi: {
      title: 'Varauspyyntö',
      from: 'Lähettäjä',
      service: 'Palvelu',
      date: 'Pyydetty päivä',
      time: 'Toivottu aika',
      location: 'Sijainti',
      description: 'Kuvaus',
      urgency: 'Kiireellisyys',
      quickResponse: 'Pikavastaus',
      accept: 'Hyväksy',
      decline: 'Hylkää',
      proposeNewTime: 'Ehdota uutta aikaa',
      accepted: 'Hyväksyit tämän varauksen',
      declined: 'Hylkäsit tämän varauksen',
      proposed: 'Ehdotit vaihtoehtoista aikaa',
      waitingClient: 'Odotetaan asiakkaan hyväksyntää',
      proposeTitle: 'Ehdota vaihtoehtoista aikaa',
      proposeDate: 'Uusi päivä',
      proposeTime: 'Uusi aika',
      proposeMessage: 'Viesti asiakkaalle',
      proposePrice: 'Arvioitu hinta (valinnainen)',
      submit: 'Lähetä ehdotus',
      cancel: 'Peruuta',
      expired: 'Tämä varauslinkki on vanhentunut',
      alreadyResponded: 'Olet jo vastannut tähän pyyntöön'
    }
  };

  const l = labels[language] || labels.en;

  const timeSlots = [
    { value: 'morning', label: t('booking.time.morning') || 'Morning (8-12)' },
    { value: 'afternoon', label: t('booking.time.afternoon') || 'Afternoon (12-17)' },
    { value: 'evening', label: t('booking.time.evening') || 'Evening (17-21)' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500 mb-4" />
          <p className="text-muted-foreground">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{l.expired}</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToHome') || 'Back to Home'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      {/* Header */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" className="text-2xl font-bold text-orange-500">
            KEIKKA
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="overflow-hidden">
            {/* Quick Response Badge */}
            {booking?.quick_response && (
              <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                <span className="font-medium">{l.quickResponse}</span>
              </div>
            )}

            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-orange-500" />
                  {l.title}
                </CardTitle>
                {booking?.urgency && booking.urgency !== 'normal' && (
                  <Badge className={cn(
                    booking.urgency === 'asap' ? 'bg-red-100 text-red-700' :
                    booking.urgency === 'today' ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-100 text-blue-700'
                  )}>
                    {booking.urgency.toUpperCase()}
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Booking Details */}
              <div className="grid gap-4">
                {/* Client Info */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <User className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{l.from}</p>
                    <p className="font-medium">{booking?.contact_name || 'Client'}</p>
                    {booking?.contact_email && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <Mail className="h-3.5 w-3.5" />
                        {booking.contact_email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Service */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">{l.service}</p>
                    <p className="font-medium">{booking?.category}</p>
                    {booking?.subcategory && (
                      <p className="text-sm text-slate-500">{booking.subcategory}</p>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">{l.location}</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {booking?.city}, {booking?.country}
                    </p>
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <p className="text-sm text-orange-600 mb-1">{l.date}</p>
                    <p className="font-bold text-orange-700">{booking?.preferred_date}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <p className="text-sm text-orange-600 mb-1">{l.time}</p>
                    <p className="font-bold text-orange-700">{booking?.preferred_time || 'Flexible'}</p>
                  </div>
                </div>

                {/* Description */}
                {booking?.description && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">{l.description}</p>
                    <p className="text-slate-700">{booking.description}</p>
                  </div>
                )}
              </div>

              {/* Response Status or Action Buttons */}
              {responseSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={cn(
                    "p-6 rounded-xl text-center",
                    responseType === 'accept' ? "bg-green-50 border-2 border-green-200" :
                    responseType === 'decline' ? "bg-red-50 border-2 border-red-200" :
                    "bg-blue-50 border-2 border-blue-200"
                  )}
                >
                  {responseType === 'accept' && (
                    <>
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <h3 className="text-xl font-bold text-green-700">{l.accepted}</h3>
                    </>
                  )}
                  {responseType === 'decline' && (
                    <>
                      <X className="h-12 w-12 text-red-500 mx-auto mb-3" />
                      <h3 className="text-xl font-bold text-red-700">{l.declined}</h3>
                    </>
                  )}
                  {responseType === 'propose' && (
                    <>
                      <Clock className="h-12 w-12 text-blue-500 mx-auto mb-3" />
                      <h3 className="text-xl font-bold text-blue-700">{l.proposed}</h3>
                      <p className="text-blue-600 mt-2">{l.waitingClient}</p>
                    </>
                  )}
                </motion.div>
              ) : (
                <div className="space-y-4">
                  {/* Primary Actions */}
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      size="lg"
                      className="h-16 text-lg bg-green-500 hover:bg-green-600"
                      onClick={handleAccept}
                      disabled={responding}
                    >
                      {responding ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-6 w-6 mr-2" />
                          {l.accept}
                        </>
                      )}
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-16 text-lg border-red-200 text-red-600 hover:bg-red-50"
                      onClick={handleDecline}
                      disabled={responding}
                    >
                      {responding ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <X className="h-6 w-6 mr-2" />
                          {l.decline}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Secondary Action */}
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setShowProposeModal(true)}
                    disabled={responding}
                  >
                    <Clock className="h-5 w-5 mr-2" />
                    {l.proposeNewTime}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Propose New Time Modal */}
      <Dialog open={showProposeModal} onOpenChange={setShowProposeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{l.proposeTitle}</DialogTitle>
            <DialogDescription>
              {t('booking.response.proposeDesc') || 'Suggest an alternative date and time for this booking.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date Picker */}
            <div className="space-y-2">
              <Label>{l.proposeDate}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !proposedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {proposedDate ? format(proposedDate, 'PPP') : t('booking.pickDate') || 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={proposedDate}
                    onSelect={setProposedDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Selector */}
            <div className="space-y-2">
              <Label>{l.proposeTime}</Label>
              <Select value={proposedTime} onValueChange={setProposedTime}>
                <SelectTrigger>
                  <SelectValue placeholder={t('booking.selectTime') || 'Select time'} />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(slot => (
                    <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estimated Price */}
            <div className="space-y-2">
              <Label>{l.proposePrice}</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  placeholder="0.00"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label>{l.proposeMessage}</Label>
              <Textarea
                placeholder={t('booking.response.messagePlaceholder') || 'Add a message to the client...'}
                value={proposedMessage}
                onChange={(e) => setProposedMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProposeModal(false)}>
              {l.cancel}
            </Button>
            <Button onClick={handlePropose} disabled={responding}>
              {responding ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MessageSquare className="h-4 w-4 mr-2" />
              )}
              {l.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderResponsePage;
